import { Button } from '@/components/ui/button'
import { Textarea } from '@/components/ui/textarea'
import { openGlobalChat } from '@/features/global-chat/global-chat-state'
import { Jobibot } from '@/ui/rapid-ui'
import { logger } from '@/ui/utilities/instrumentation/logger'
import { SendHorizonal } from 'lucide-react'
import { useCallback, useState } from 'react'
import { v4 as uuidv4 } from 'uuid'

type JobibotInputProps = {
	placeholder?: string
	disabled?: boolean
	source?: string
}

export function JobibotInput({ placeholder = 'Ask Jobibot…', disabled, source }: JobibotInputProps) {
	const [value, setValue] = useState('')

	const handleSubmit = useCallback(() => {
		const text = value.trim()
		if (!text || disabled) { return }
		logger.track('Jobibot: Input submitted', { data: { source: source ?? 'unknown' } })
		openGlobalChat({ conversationId: uuidv4(), prefillText: text })
		setValue('')
	}, [value, disabled, source])

	const handleKeyDown = useCallback((e: React.KeyboardEvent<HTMLTextAreaElement>) => {
		if (e.key === 'Enter' && !e.shiftKey) {
			e.preventDefault()
			handleSubmit()
		}
	}, [handleSubmit])

	return (
		<div className="flex items-end gap-2 rounded-xl border border-input bg-background px-3 py-2 transition-[border-color,box-shadow] focus-within:border-ring focus-within:ring-1 focus-within:ring-ring">
			<div className="shrink-0 mb-1">
				<Jobibot size={18} />
			</div>
			<Textarea
				value={value}
				onChange={(e) => setValue(e.target.value)}
				onKeyDown={handleKeyDown}
				placeholder={placeholder}
				disabled={disabled}
				rows={1}
				className="resize-none border-0 p-0 min-h-[28px] max-h-[120px] shadow-none focus-visible:ring-0 bg-transparent text-sm leading-loose"
			/>
			<Button
				type="button"
				size="icon"
				disabled={!value.trim() || disabled}
				onClick={handleSubmit}
				className="shrink-0 h-7 w-7 mb-0.5 bg-linear-to-br from-indigo-700 via-indigo-600 via-40% to-brand-600 text-white border-0 disabled:opacity-40"
			>
				<SendHorizonal className="h-3.5 w-3.5" />
			</Button>
		</div>
	)
}
